import type {
  Appearance,
  Connector,
  Diagram,
  Point,
  Relationship,
  Side,
  Operation,
  Preferences,
  RelationshipLabelStyle,
} from "../../shared/contracts.js";

import { componentLayout } from "./canvasLayoutModes.js";

export interface CanvasBox {
  id: string;
  objectId: string;
  x: number;
  y: number;
  width: number;
  height: number;
  pinned?: boolean;
  group?: string;
}
export interface Attachment {
  side: Side;
  slot: number;
  count: number;
  point: Point;
}
export interface RoutedRelationship {
  relationship: Relationship;
  source: CanvasBox;
  target: CanvasBox;
  sourceAttachment: Attachment;
  targetAttachment: Attachment;
  points: Point[];
  warnings: string[];
  length: number;
}
export interface LayoutOptions {
  mode: "hierarchical" | "clustered" | "tree" | "circular" | "orthogonal" | "symmetric";
  direction: "down" | "up" | "right" | "left";
  gap: number;
  scopeIds?: string[];
  rootId?: string;
}
export interface LayoutCandidate {
  positions: Record<string, Point>;
  moved: number;
  overlaps: number;
  warnings: string[];
  clusters: number;
  fingerprint: string;
}
export const SIDES: Side[] = ["top", "right", "bottom", "left"];
const EPS = 0.01;
export const normal = (side: Side): Point =>
  side === "top"
    ? { x: 0, y: -1 }
    : side === "bottom"
      ? { x: 0, y: 1 }
      : side === "left"
        ? { x: -1, y: 0 }
        : { x: 1, y: 0 };
export const distance = (a: Point, b: Point) =>
  Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
export function pointOnSide(
  box: CanvasBox,
  side: Side,
  slot: number,
  count: number,
): Point {
  const span = side === "left" || side === "right" ? box.height : box.width;
  const inset = Math.min(22, span / 4);
  const fraction =
    count <= 1
      ? 0.5
      : (Math.max(0, Math.min(count - 1, slot)) + 1) / (count + 1);
  const along = inset + fraction * (span - inset * 2);
  return side === "top"
    ? { x: box.x + along, y: box.y }
    : side === "bottom"
      ? { x: box.x + along, y: box.y + box.height }
      : side === "left"
        ? { x: box.x, y: box.y + along }
        : { x: box.x + box.width, y: box.y + along };
}
export function nearestAttachment(
  box: CanvasBox,
  point: Point,
  count: number,
  occupied: Set<string> = new Set(),
): Attachment {
  const candidates = SIDES.flatMap((side) =>
    Array.from({ length: Math.max(1, count) }, (_, slot) => ({
      side,
      slot,
      count: Math.max(1, count),
      point: pointOnSide(box, side, slot, Math.max(1, count)),
    })),
  );
  const free = candidates.filter((a) => !occupied.has(`${a.side}:${a.slot}`));
  return (free.length ? free : candidates).sort(
    (a, b) => distance(a.point, point) - distance(b.point, point),
  )[0]!;
}
export function inside(point: Point, box: CanvasBox, padding = 0) {
  return (
    point.x > box.x - padding + EPS &&
    point.x < box.x + box.width + padding - EPS &&
    point.y > box.y - padding + EPS &&
    point.y < box.y + box.height + padding - EPS
  );
}
export function segmentHitsBox(
  a: Point,
  b: Point,
  box: CanvasBox,
  padding = 0,
) {
  if (Math.abs(a.x - b.x) < EPS)
    return (
      a.x > box.x - padding + EPS &&
      a.x < box.x + box.width + padding - EPS &&
      Math.max(a.y, b.y) > box.y - padding + EPS &&
      Math.min(a.y, b.y) < box.y + box.height + padding - EPS
    );
  if (Math.abs(a.y - b.y) < EPS)
    return (
      a.y > box.y - padding + EPS &&
      a.y < box.y + box.height + padding - EPS &&
      Math.max(a.x, b.x) > box.x - padding + EPS &&
      Math.min(a.x, b.x) < box.x + box.width + padding - EPS
    );
  return true;
}
export function simplifyPath(points: Point[]) {
  const out: Point[] = [];
  for (const point of points) {
    if (out.length && distance(out[out.length - 1]!, point) < EPS) continue;
    const a = out[out.length - 2],
      b = out[out.length - 1];
    if (
      a &&
      b &&
      ((Math.abs(a.x - b.x) < EPS && Math.abs(b.x - point.x) < EPS) ||
        (Math.abs(a.y - b.y) < EPS && Math.abs(b.y - point.y) < EPS))
    ) {
      // Retain a reversal: removing it can cut through an endpoint node.
      if (
        (b.x - a.x) * (point.x - b.x) >= 0 &&
        (b.y - a.y) * (point.y - b.y) >= 0
      )
        out.pop();
    }
    out.push({ x: point.x, y: point.y });
  }
  return out;
}
const pathLength = (p: Point[]) =>
  p.slice(1).reduce((n, b, i) => n + distance(p[i]!, b), 0);
const isClear = (points: Point[], obstacles: CanvasBox[]) =>
  points
    .slice(1)
    .every((b, i) =>
      obstacles.every((o) => !segmentHitsBox(points[i]!, b, o, 8)),
    );

class MinHeap {
  private data: { key: number; score: number }[] = [];
  push(item: { key: number; score: number }) {
    let i = this.data.push(item) - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (this.data[p]!.score <= item.score) break;
      this.data[i] = this.data[p]!;
      i = p;
    }
    this.data[i] = item;
  }
  pop() {
    const first = this.data[0];
    const last = this.data.pop();
    if (!first || !this.data.length || !last) return first;
    let i = 0;
    while (i * 2 + 1 < this.data.length) {
      let c = i * 2 + 1;
      if (
        c + 1 < this.data.length &&
        this.data[c + 1]!.score < this.data[c]!.score
      )
        c++;
      if (this.data[c]!.score >= last.score) break;
      this.data[i] = this.data[c]!;
      i = c;
    }
    this.data[i] = last;
    return first;
  }
  get size() {
    return this.data.length;
  }
}

/** Bounded rectilinear visibility grid. Direct candidates are tested first, then A* with bend costs. */
export interface RoutingOptions { optimizeCrossings?: boolean; centerRelationshipEndpoints?: boolean; avoidParentTopAttachment?: boolean; relationshipLaneGap?: number }
/** Parallel lanes closer than the requested diagram-space clearance are scored
 * even if they do not literally overlap. Endpoints themselves remain unconstrained. */
export function parallelRouteCrowding(a:readonly Point[],b:readonly Point[],gap=18):number {
  let score=0;
  for(let i=1;i<a.length;i++)for(let j=1;j<b.length;j++){
    const a0=a[i-1]!,a1=a[i]!,b0=b[j-1]!,b1=b[j]!;
    const ah=Math.abs(a0.y-a1.y)<EPS,bh=Math.abs(b0.y-b1.y)<EPS;
    if(ah!==bh)continue;
    const fixed=ah?'y':'x',axis=ah?'x':'y',distance=Math.abs(a0[fixed]-b0[fixed]);
    if(distance>=gap)continue;
    const length=Math.max(0,Math.min(Math.max(a0[axis],a1[axis]),Math.max(b0[axis],b1[axis]))-Math.max(Math.min(a0[axis],a1[axis]),Math.min(b0[axis],b1[axis])));
    score+=length*(gap-distance)/gap;
  }
  return score;
}

export function orthogonalRoute(
  start: Point,
  end: Point,
  obstacles: CanvasBox[],
  lane = 0,
  previousRoutes: readonly Point[][] = [],
  laneGap = 18,
): { points: Point[]; blocked: boolean } {
  const midX = (start.x + end.x) / 2 + lane * 10,
    midY = (start.y + end.y) / 2 + lane * 10;
  const candidates: Point[][] = [
    [start, { x: start.x, y: end.y }, end],
    [start, { x: end.x, y: start.y }, end],
    [start, { x: midX, y: start.y }, { x: midX, y: end.y }, end],
    [start, { x: start.x, y: midY }, { x: end.x, y: midY }, end],
  ];
  // Additional parallel channels provide useful alternatives before invoking the
  // visibility grid. Soft crossing costs do not override obstacle avoidance.
  for (const offset of [-72, -36, 36, 72]) {
    candidates.push([start, { x: midX + offset, y: start.y }, { x: midX + offset, y: end.y }, end]);
    candidates.push([start, { x: start.x, y: midY + offset }, { x: end.x, y: midY + offset }, end]);
  }
  // Bound soft crossing work on dense diagrams; all node obstacles remain hard constraints.
  const recentRoutes = previousRoutes.slice(-128);
  const routeScore = (points: Point[]) => pathLength(points) + points.length * 16 +
    recentRoutes.reduce((sum, previous) => sum + countRouteCrossings([{ points }, { points: previous }]) * 900 + parallelRouteCrowding(points, previous, laneGap) * 10, 0);
  const clear = candidates
    .map(simplifyPath)
    .filter((p) => isClear(p, obstacles))
    .map((points) => ({ points, score: routeScore(points) }))
    .sort((a, b) => a.score - b.score)
    .map((candidate) => candidate.points);
  if (clear[0]) return { points: clear[0], blocked: false };
  // Limit coordinate growth; all obstacles are still checked before accepting any segment.
  const relevant = [...obstacles]
    .sort(
      (a, b) =>
        distance(
          { x: a.x + a.width / 2, y: a.y + a.height / 2 },
          { x: midX, y: midY },
        ) -
        distance(
          { x: b.x + b.width / 2, y: b.y + b.height / 2 },
          { x: midX, y: midY },
        ),
    )
    .slice(0, 32);
  const xs = [
    ...new Set([
      start.x,
      end.x,
      ...relevant.flatMap((o) => [o.x - 12, o.x + o.width + 12]),
    ]),
  ].sort((a, b) => a - b);
  const ys = [
    ...new Set([
      start.y,
      end.y,
      ...relevant.flatMap((o) => [o.y - 12, o.y + o.height + 12]),
    ]),
  ].sort((a, b) => a - b);
  const width = xs.length,
    pointCount = width * ys.length,
    endIndex = ys.indexOf(end.y) * width + xs.indexOf(end.x),
    startIndex = ys.indexOf(start.y) * width + xs.indexOf(start.x);
  const costs = new Float64Array(pointCount * 3).fill(Infinity),
    previous = new Int32Array(pointCount * 3).fill(-1),
    closed = new Uint8Array(pointCount * 3);
  const pointAt = (index: number): Point => ({
    x: xs[index % width]!,
    y: ys[Math.floor(index / width)]!,
  });
  const heap = new MinHeap(),
    initialKey = startIndex * 3;
  costs[initialKey] = 0;
  heap.push({ key: initialKey, score: distance(start, end) });
  let found = -1,
    budget = pointCount * 3;
  while (heap.size && budget-- > 0) {
    const current = heap.pop()!;
    if (closed[current.key]) continue;
    closed[current.key] = 1;
    const index = Math.floor(current.key / 3),
      dir = current.key % 3;
    if (index === endIndex) {
      found = current.key;
      break;
    }
    const x = index % width,
      y = Math.floor(index / width),
      a = pointAt(index);
    const neighbors = [
      [x - 1, y, 1],
      [x + 1, y, 1],
      [x, y - 1, 2],
      [x, y + 1, 2],
    ];
    for (const [nx, ny, nd] of neighbors) {
      if (nx! < 0 || nx! >= width || ny! < 0 || ny! >= ys.length) continue;
      const ni = ny! * width + nx!,
        b = pointAt(ni),
        key = ni * 3 + nd!;
      if (closed[key] || obstacles.some((o) => segmentHitsBox(a, b, o, 8)))
        continue;
      const cost =
        costs[current.key]! +
        distance(a, b) +
        (dir !== 0 && dir !== nd ? 22 : 0);
      if (cost >= costs[key]!) continue;
      costs[key] = cost;
      previous[key] = current.key;
      heap.push({ key, score: cost + distance(b, end) });
    }
  }
  if (found >= 0) {
    const points: Point[] = [];
    for (let k = found; k >= 0; k = previous[k]!)
      points.push(pointAt(Math.floor(k / 3)));
    return { points: simplifyPath(points.reverse()), blocked: false };
  }
  return { points: simplifyPath(candidates[2]!), blocked: true };
}

export function allocateRelationships(
  boxes: CanvasBox[], relationships: Relationship[], connectors: Connector[],
  options: RoutingOptions = {},
): Omit<RoutedRelationship, "points" | "warnings" | "length">[] {
  const byObject = new Map<string, CanvasBox>();
  [...boxes].sort((a, b) => a.id.localeCompare(b.id)).forEach((box) => { if (!byObject.has(box.objectId)) byObject.set(box.objectId, box); });
  const connectorById = new Map(connectors.map((connector) => [connector.relationshipId, connector]));
  const visible = relationships.filter((relationship) => byObject.has(relationship.sourceObjectId) && byObject.has(relationship.targetObjectId))
    .sort((a, b) => a.relationshipId.localeCompare(b.relationshipId));
  type Endpoint = { key: string; box: CanvasBox; other: CanvasBox; side: Side; preferred?: number; explicit: boolean };
  const endpoints: Endpoint[] = [];
  visible.forEach((relationship) => {
    const connector = connectorById.get(relationship.relationshipId);
    const source = byObject.get(relationship.sourceObjectId)!, target = byObject.get(relationship.targetObjectId)!;
    for (const isSource of [true, false]) {
      const box = isSource ? source : target, other = isSource ? target : source;
      const explicitSide = isSource ? connector?.sourceSide : connector?.targetSide;
      const dx = other.x + other.width / 2 - box.x - box.width / 2;
      const dy = other.y + other.height / 2 - box.y - box.height / 2;
      const side: Side = explicitSide ?? (box.id === other.id ? isSource ? "right" : "bottom"
        : Math.abs(dx) > Math.abs(dy) * 0.85 ? dx > 0 ? "right" : "left" : dy > 0 ? "bottom" : "top");
      endpoints.push({ key: `${relationship.relationshipId}:${isSource ? "source" : "target"}`, box, other, side,
        preferred: isSource ? connector?.sourceSlot : connector?.targetSlot, explicit: explicitSide !== undefined });
    }
  });
  const attachmentByKey = new Map<string, Attachment>();
  const incident = new Map<string, Endpoint[]>();
  endpoints.forEach((endpoint) => incident.set(endpoint.box.id, [...(incident.get(endpoint.box.id) ?? []), endpoint]));
  for (const endpointsAtBox of incident.values()) {
    const count = endpointsAtBox.length;
    for (const side of SIDES) {
      const peers = endpointsAtBox.filter((endpoint) => endpoint.side === side);
      const horizontal = side === "top" || side === "bottom";
      const coordinate = (endpoint: Endpoint) => horizontal ? endpoint.other.x + endpoint.other.width / 2 : endpoint.other.y + endpoint.other.height / 2;
      peers.sort((a, b) => coordinate(a) - coordinate(b) || a.key.localeCompare(b.key));
      const spatialOrder = new Map(peers.map((endpoint, i) => [endpoint.key, i]));
      // Reserve explicit choices before any automatic endpoint. Remaining slots
      // follow the geometric order of neighbors, avoiding crossed fan-outs.
      peers.sort((a, b) => Number(b.preferred !== undefined) - Number(a.preferred !== undefined) || Number(b.explicit) - Number(a.explicit) || spatialOrder.get(a.key)! - spatialOrder.get(b.key)!);
      const occupied = new Set<number>();
      for (const endpoint of peers) {
        const preferred = endpoint.preferred ?? (peers.length === 1 ? (count - 1) / 2 : (spatialOrder.get(endpoint.key)! + 1) * (count + 1) / (peers.length + 1) - 1);
        const slots = Array.from({ length: count }, (_, i) => i).sort((a, b) => Math.abs(a - preferred) - Math.abs(b - preferred) || a - b);
        const slot = slots.find((index) => !occupied.has(index)) ?? 0;
        occupied.add(slot);
        attachmentByKey.set(endpoint.key, { side, slot, count, point: endpoint.preferred === undefined && !endpoint.explicit && peers.length > 1 && peers.every(peer=>peer.other.id===endpoint.other.id)
          ? pointOnSide(endpoint.box, side, spatialOrder.get(endpoint.key)! * 3, (peers.length - 1) * 3 + 1)
          : pointOnSide(endpoint.box, side, slot, count) });
      }
    }
  }
  const allocated = visible.map((relationship) => ({ relationship,
    source: byObject.get(relationship.sourceObjectId)!, target: byObject.get(relationship.targetObjectId)!,
    sourceAttachment: attachmentByKey.get(`${relationship.relationshipId}:source`)!,
    targetAttachment: attachmentByKey.get(`${relationship.relationshipId}:target`)!,
  }));
  for (const edge of allocated) {
    const connector = connectorById.get(edge.relationship.relationshipId);
    for (const end of ["source", "target"] as const) {
      const fraction = connector?.[`${end}Fraction`];
      if (fraction !== undefined && Number.isFinite(fraction)) {
        const attachment = edge[`${end}Attachment`], box = edge[end];
        const horizontal = attachment.side === "top" || attachment.side === "bottom";
        attachment.point = { ...attachment.point, [horizontal ? "x" : "y"]:
          (horizontal ? box.x : box.y) + Math.max(0, Math.min(1, fraction)) * (horizontal ? box.width : box.height) };
      }
    }
    // A free, facing pair can share an ordinate instead of keeping stale centered
    // ports and a needless dogleg. Explicit/manual attachment choices stay fixed.
    const a = edge.sourceAttachment, b = edge.targetAttachment;
    if (options.centerRelationshipEndpoints === false || connector?.routing === "manual" || connector?.sourceSide || connector?.targetSide ||
        connector?.sourceSlot !== undefined || connector?.targetSlot !== undefined ||
        connector?.sourceFraction !== undefined || connector?.targetFraction !== undefined || edge.source.id === edge.target.id) continue;
    const horizontal = (a.side === "right" && b.side === "left") || (a.side === "left" && b.side === "right");
    const vertical = (a.side === "bottom" && b.side === "top") || (a.side === "top" && b.side === "bottom");
    if (!horizontal && !vertical) continue;
    const axis = horizontal ? "y" : "x", size = horizontal ? "height" : "width";
    const lo = Math.max(edge.source[axis] + Math.min(22, edge.source[size] / 4), edge.target[axis] + Math.min(22, edge.target[size] / 4));
    const hi = Math.min(edge.source[axis] + edge.source[size] - Math.min(22, edge.source[size] / 4), edge.target[axis] + edge.target[size] - Math.min(22, edge.target[size] / 4));
    if (lo > hi) continue;
    const quiet = a.count === 1 ? a : b.count === 1 ? b : undefined;
    const busy = quiet === a ? b : a;
    if(!quiet)continue;
    const coordinate=quiet.point[axis];
    if(coordinate<lo||coordinate>hi)continue;
    const busyBox=quiet===a?edge.target:edge.source;
    const clearPort=allocated.every(other=>other===edge||(['source','target'] as const).every(end=>other[end].id!==busyBox.id||other[`${end}Attachment`].side!==busy.side||Math.abs(other[`${end}Attachment`].point[axis]-coordinate)>=(options.relationshipLaneGap??18)));
    // Keep the quiet endpoint centered. A busy endpoint may share that ordinate
    // only if its port retains real clearance from the other incident routes.
    if(busy.count===1||clearPort)busy.point={...busy.point,[axis]:coordinate};
  }
  return allocated;
}
type AllocatedRelationship = Omit<RoutedRelationship, "points" | "warnings" | "length">;

function buildRelationshipRoute(
  edge: AllocatedRelationship, index: number, boxes: CanvasBox[], connector: Connector | undefined,
  previousRoutes: Point[][], requestedTerminal?: number, options:RoutingOptions = {},
): RoutedRelationship {
  const { sourceAttachment: a, targetAttachment: b } = edge, na = normal(a.side), nb = normal(b.side);
  // Manual terminals stay stable across unrelated relationship insertions.
  // Automatic channels stagger departure distances to keep parallel legs apart.
  const terminal = requestedTerminal ?? (connector?.routing === "manual" ? 28 : 28 + Math.max(a.count>1?a.slot:0,b.count>1?b.slot:0) * (options.relationshipLaneGap??18));
  const start = { x: a.point.x + na.x * terminal, y: a.point.y + na.y * terminal };
  const end = { x: b.point.x + nb.x * terminal, y: b.point.y + nb.y * terminal };
  const manual = connector?.routing === "manual";
  // Close facing entities need no outward doglegs. Fixed 28px terminals would
  // overshoot each other in a narrow but valid gap and create backtracking.
  const dx=b.point.x-a.point.x,dy=b.point.y-a.point.y;
  if ((!manual || !(connector.waypoints?.length)) &&
      (Math.abs(dx)<EPS || Math.abs(dy)<EPS) && dx*na.x+dy*na.y>0 && -dx*nb.x-dy*nb.y>0 &&
      routeAvoidsEntities([a.point,b.point],boxes)) return {...edge,points:[a.point,b.point],warnings:[],length:distance(a.point,b.point)};
  const stops = [start, ...(manual ? connector.waypoints ?? [] : []), end];
  const points = [a.point], warnings: string[] = [];
  for (let i = 1; i < stops.length; i++) {
    const part = orthogonalRoute(stops[i - 1]!, stops[i]!, boxes, (index % 3) - 1, manual ? [] : previousRoutes, options.relationshipLaneGap??18);
    points.push(...part.points);
    if (part.blocked) warnings.push("A fixed endpoint or waypoint prevents an obstacle-free route. Move a waypoint or change its side.");
  }
  points.push(b.point);
  const path = simplifyPath(points);
  if (boxes.some((o) => o.id !== edge.source.id && segmentHitsBox(a.point, start, o)) ||
      boxes.some((o) => o.id !== edge.target.id && segmentHitsBox(end, b.point, o)))
    warnings.push("An endpoint terminal intersects a neighboring entity. Increase spacing or change its attachment side.");
  return { ...edge, points: path, warnings: [...new Set(warnings)], length: pathLength(path) };
}

/** Collinear overlap is scored separately: trading crossings for stacked lines is
 * not a routing improvement. Shared endpoints alone contribute zero. */
export function sharedRouteLength(a: Point[], b: Point[]): number {
  let total = 0;
  for (let i = 1; i < a.length; i++) for (let j = 1; j < b.length; j++) {
    const a0 = a[i - 1]!, a1 = a[i]!, b0 = b[j - 1]!, b1 = b[j]!;
    const ah = Math.abs(a0.y - a1.y) < EPS, bh = Math.abs(b0.y - b1.y) < EPS;
    if (ah !== bh) continue;
    const fixed = ah ? "y" : "x", axis = ah ? "x" : "y";
    if (Math.abs(a0[fixed] - b0[fixed]) >= EPS) continue;
    total += Math.max(0, Math.min(Math.max(a0[axis], a1[axis]), Math.max(b0[axis], b1[axis])) -
      Math.max(Math.min(a0[axis], a1[axis]), Math.min(b0[axis], b1[axis])));
  }
  return total;
}

export function routeRelationships(
  boxes: CanvasBox[], relationships: Relationship[], connectors: Connector[],
  options: RoutingOptions = {},
): RoutedRelationship[] {
  const connectorById = new Map(connectors.map((connector) => [connector.relationshipId, connector]));
  const allocated = allocateRelationships(boxes, relationships, connectors, options).sort((a, b) =>
    Number(connectorById.get(b.relationship.relationshipId)?.routing === "manual") - Number(connectorById.get(a.relationship.relationshipId)?.routing === "manual") ||
    distance(a.sourceAttachment.point, a.targetAttachment.point) - distance(b.sourceAttachment.point, b.targetAttachment.point) ||
    a.relationship.relationshipId.localeCompare(b.relationship.relationshipId));
  const routes: RoutedRelationship[] = [];
  allocated.forEach((edge, index) => routes.push(buildRelationshipRoute(edge, index, boxes,
    connectorById.get(edge.relationship.relationshipId), options.optimizeCrossings === false ? [] : routes.map((route) => route.points), undefined, options)));
  // Side preferences are soft. Entity avoidance wins, followed by a short clear
  // route; the semantic parent's top is used when alternatives need a detour.
  for(let index=0;index<routes.length;index++){
    const old=routes[index]!,connector=connectorById.get(old.relationship.relationshipId);
    if(connector?.routing==='manual')continue;
    const parentEnd=old.relationship.sourceCardinality==='one'&&old.relationship.targetCardinality!=='one'?'source':'target';
    const parentTop=options.avoidParentTopAttachment!==false&&old[`${parentEnd}Attachment`].side==='top';
    if(!old.warnings.length&&routeAvoidsEntities(old.points,boxes)&&!parentTop)continue;
    const others=routes.filter((_,i)=>i!==index);
    const penalty=(route:RoutedRelationship)=>route.length+Math.max(0,route.points.length-2)*20+others.reduce((n,r)=>n+countRouteCrossings([route,r])*900+parallelRouteCrowding(route.points,r.points,options.relationshipLaneGap??18)*10,0)+(options.avoidParentTopAttachment!==false&&route[`${parentEnd}Attachment`].side==='top'?32:0);
    let best=old,bestScore=old.warnings.length||!routeAvoidsEntities(old.points,boxes)?Infinity:penalty(old);
    for(const end of ['source','target'] as const){
      if(connector?.[`${end}Side`]!==undefined||connector?.[`${end}Slot`]!==undefined||connector?.[`${end}Fraction`]!==undefined)continue;
      for(const side of SIDES){
        if(side===old[`${end}Attachment`].side)continue;
        const box=old[end],peers=routes.filter(r=>r!==old).flatMap(r=>(['source','target'] as const).filter(e=>r[e].id===box.id&&r[`${e}Attachment`].side===side).map(e=>r[`${e}Attachment`].point));
        const point=pointOnSide(box,side,0,1),axis=side==='left'||side==='right'?'y':'x';
        if(peers.some(p=>Math.abs(p[axis]-point[axis])<(options.relationshipLaneGap??18)))continue;
        const otherPoint=old[end==='source'?'targetAttachment':'sourceAttachment'].point;
        if(Math.abs(point.x-otherPoint.x)+Math.abs(point.y-otherPoint.y)>=bestScore)continue;
        const edge={...allocated[index]!,[`${end}Attachment`]:{side,slot:0,count:old[`${end}Attachment`].count,point}};
        const trial=buildRelationshipRoute(edge,index,boxes,connector,others.map(r=>r.points),undefined,options);
        const score=penalty(trial);
        if(!trial.warnings.length&&routeAvoidsEntities(trial.points,boxes)&&score<bestScore){best=trial;bestScore=score;}
      }
    }
    if(best!==old){routes[index]=best;allocated[index]=best;}
  }
  // A bounded rip-up pass gives early routes knowledge of later routes, allowing
  // them to exchange channels rather than freezing a poor first-fit decision.
  // Never rip up manual geometry or override an explicitly chosen attachment.
  if (options.optimizeCrossings !== false && routes.length <= 120) {
    for (let pass = 0; pass < 2; pass++) {
      let improved = false;
      const order = routes.map((route, index) => ({ index, crossings: routes.reduce((sum, other, j) => sum + (j === index ? 0 : countRouteCrossings([route, other])), 0) }))
        .filter(({ crossings }) => crossings > 0).sort((a, b) => b.crossings - a.crossings || a.index - b.index).slice(0, pass === 0 ? 24 : 8);
      for (const { index } of order) {
        const old = routes[index]!, connector = connectorById.get(old.relationship.relationshipId);
        if (connector?.routing === "manual") continue;
        const others = routes.filter((_, i) => i !== index);
        const collisions = (route: RoutedRelationship) => others.reduce((sum, other) => sum + countRouteCrossings([route, other]), 0);
        const overlap = (route: RoutedRelationship) => others.reduce((sum, other) => sum + sharedRouteLength(route.points, other.points), 0);
        let best = old, bestCrossings = collisions(old);
        const oldOverlap = overlap(old);
        for (const terminal of [28, 36, 44]) {
          const candidate = buildRelationshipRoute(allocated[index]!, index + pass + 1, boxes, connector, others.map((route) => route.points), terminal, options);
          const nextCrossings = collisions(candidate), nextOverlap = overlap(candidate);
          // Keep node avoidance absolute, then reduce crossings without disguising
          // them as a long overlap or an unbounded excursion outside the diagram.
          if (!candidate.warnings.length && nextCrossings < bestCrossings &&
              nextOverlap <= oldOverlap + 24 && candidate.length <= old.length * 1.6 + 160) {
            best = candidate; bestCrossings = nextCrossings;
          }
          if (!bestCrossings) break;
        }
        if (best !== old) { routes[index] = best; improved = true; }
      }
      if (!improved) break;
    }
  }
  // Exchange adjacent automatic attachment positions only when measured route
  // crossings improve. Identity/cardinality stay with the original relationship.
  if (options.optimizeCrossings !== false && routes.length <= 120 && countRouteCrossings(routes)) {
    const buckets = new Map<string, { index: number; end: "source" | "target" }[]>();
    routes.forEach((route, index) => {
      const connector = connectorById.get(route.relationship.relationshipId);
      for (const end of ["source", "target"] as const) {
        if (connector?.routing === "manual" || connector?.[`${end}Side`] !== undefined ||
            connector?.[`${end}Slot`] !== undefined || connector?.[`${end}Fraction`] !== undefined) continue;
        const key = `${route[end].id}:${route[`${end}Attachment`].side}`;
        buckets.set(key, [...(buckets.get(key) ?? []), { index, end }]);
      }
    });
    let attempts = 0;
    for (const entries of buckets.values()) {
      entries.sort((a, b) => routes[a.index]![`${a.end}Attachment`].slot - routes[b.index]![`${b.end}Attachment`].slot);
      for (let i = 1; i < entries.length && attempts < 24; i++) {
        const a = entries[i - 1]!, b = entries[i]!;
        if (a.index === b.index) continue;
        const oldA = routes[a.index]!, oldB = routes[b.index]!;
        const others = routes.filter((_, index) => index !== a.index && index !== b.index);
        const crossings = (x: RoutedRelationship, y: RoutedRelationship) => countRouteCrossings([x, y]) + others.reduce((sum, route) => sum + countRouteCrossings([x, route]) + countRouteCrossings([y, route]), 0);
        const oldCrossings = crossings(oldA, oldB);
        if (!oldCrossings) continue;
        attempts++;
        const nextA = { ...allocated[a.index]!, [`${a.end}Attachment`]: { ...oldB[`${b.end}Attachment`] } };
        const nextB = { ...allocated[b.index]!, [`${b.end}Attachment`]: { ...oldA[`${a.end}Attachment`] } };
        const routeA = buildRelationshipRoute(nextA, a.index, boxes, connectorById.get(oldA.relationship.relationshipId), others.map((route) => route.points), undefined, options);
        const routeB = buildRelationshipRoute(nextB, b.index, boxes, connectorById.get(oldB.relationship.relationshipId), [...others.map((route) => route.points), routeA.points], undefined, options);
        const overlap = (x: RoutedRelationship, y: RoutedRelationship) => sharedRouteLength(x.points, y.points) + others.reduce((sum, route) => sum + sharedRouteLength(x.points, route.points) + sharedRouteLength(y.points, route.points), 0);
        if (!routeA.warnings.length && !routeB.warnings.length && crossings(routeA, routeB) < oldCrossings &&
            overlap(routeA, routeB) <= overlap(oldA, oldB) + 24 && routeA.length + routeB.length <= (oldA.length + oldB.length) * 1.6 + 160) {
          allocated[a.index] = nextA; allocated[b.index] = nextB; routes[a.index] = routeA; routes[b.index] = routeB;
        }
      }
    }
  }
  // Exchange a whole anchor order as one candidate. Some crossing patterns
  // improve only when three or more endpoints move together.
  if(options.optimizeCrossings!==false&&routes.length<=80&&countRouteCrossings(routes)){
    const groups=new Map<string,{index:number;end:'source'|'target'}[]>();
    routes.forEach((route,index)=>{const connector=connectorById.get(route.relationship.relationshipId);for(const end of ['source','target'] as const){if(connector?.routing==='manual'||connector?.[`${end}Side`]!==undefined||connector?.[`${end}Slot`]!==undefined||connector?.[`${end}Fraction`]!==undefined)continue;const key=`${route[end].id}:${route[`${end}Attachment`].side}`;groups.set(key,[...(groups.get(key)??[]),{index,end}]);}});
    let budget=12;
    for(const group of groups.values()){
      if(group.length<3||group.length>12||budget--<=0)continue;
      group.sort((a,b)=>routes[a.index]![`${a.end}Attachment`].point.x-routes[b.index]![`${b.end}Attachment`].point.x||routes[a.index]![`${a.end}Attachment`].point.y-routes[b.index]![`${b.end}Attachment`].point.y);
      const ids=new Set(group.map(g=>g.index)),others=routes.filter((_,i)=>!ids.has(i));
      const oldCrossings=countRouteCrossings(routes);
      const crowding=(group:RoutedRelationship[])=>group.reduce((sum,r,i)=>sum+group.slice(i+1).reduce((n,other)=>n+parallelRouteCrowding(r.points,other.points,options.relationshipLaneGap??18),0),0);
      const oldCrowding=crowding(routes);
      for(const order of [[...group].reverse(),[...group.slice(1),group[0]!]]){
        const trial=[...routes],paths=others.map(r=>r.points);
        group.forEach((item,i)=>{const old=routes[item.index]!,peer=order[i]!;const edge={...old,[`${item.end}Attachment`]:{...routes[peer.index]![`${peer.end}Attachment`]}};const candidate=buildRelationshipRoute(edge,item.index,boxes,connectorById.get(old.relationship.relationshipId),paths,undefined,options);trial[item.index]=candidate;paths.push(candidate.points);});
        if(trial.every(r=>!r.warnings.length&&routeAvoidsEntities(r.points,boxes))&&countRouteCrossings(trial)<oldCrossings&&crowding(trial)<=oldCrowding+24&&trial.reduce((n,r)=>n+r.length,0)<=routes.reduce((n,r)=>n+r.length,0)*1.15+240){trial.forEach((r,i)=>{routes[i]=r;allocated[i]=r;});break;}
      }
    }
  }
  // Try a collective side change when channel/slot exchanges cannot untangle a
  // fan-out. Reallocate every automatic peer at that entity together. A candidate
  // is accepted only against the whole graph, never a misleading local score.
  if(options.optimizeCrossings!==false && routes.length<=80 && countRouteCrossings(routes)) {
    let budget=16;
    const ranked=routes.map((route,index)=>({index,crossings:routes.reduce((n,other,j)=>n+(j===index?0:countRouteCrossings([route,other])),0)})).filter(x=>x.crossings>0).sort((a,b)=>b.crossings-a.crossings||a.index-b.index).slice(0,4);
    for(const {index} of ranked) {
      const old=routes[index]!, connector=connectorById.get(old.relationship.relationshipId);
      if(connector?.routing==="manual")continue;
      for(const end of ["source","target"] as const) {
        if(connector?.[`${end}Side`]!==undefined || connector?.[`${end}Fraction`]!==undefined || connector?.[`${end}Slot`]!==undefined)continue;
        for(const side of SIDES.filter(side=>side!==routes[index]![`${end}Attachment`].side)) {
          if(--budget<0)break;
          const temporary={...connector,relationshipId:old.relationship.relationshipId,[`${end}Side`]:side};
          const byId=new Map(allocateRelationships(boxes,relationships,[...connectors.filter(c=>c.relationshipId!==old.relationship.relationshipId),temporary], options).map(edge=>[edge.relationship.relationshipId,edge]));
          const atBox=old[end].objectId;
          const affected=routes.map((route,i)=>({route,i})).filter(({route})=>route.relationship.sourceObjectId===atBox || route.relationship.targetObjectId===atBox);
          const affectedIds=new Set(affected.map(x=>x.i));
          const others=routes.filter((_,i)=>!affectedIds.has(i));
          const trial=[...routes]; const acceptedPaths=others.map(route=>route.points);
          for(const {route,i} of affected) {
            if(connectorById.get(route.relationship.relationshipId)?.routing==="manual")continue;
            const candidate=buildRelationshipRoute(byId.get(route.relationship.relationshipId)!,i,boxes,connectorById.get(route.relationship.relationshipId),acceptedPaths, undefined, options);
            trial[i]=candidate;acceptedPaths.push(candidate.points);
          }
          const oldLength=routes.reduce((n,r)=>n+r.length,0),newLength=trial.reduce((n,r)=>n+r.length,0);
          const shared=(group:RoutedRelationship[])=>group.reduce((sum,route,i)=>sum+group.slice(i+1).reduce((n,other)=>n+sharedRouteLength(route.points,other.points),0),0);
          if(trial.every(route=>!route.warnings.length && routeAvoidsEntities(route.points,boxes)) && countRouteCrossings(trial)<countRouteCrossings(routes) && newLength<=oldLength*1.15+240 && shared(trial)<=shared(routes)+24) {
            trial.forEach((route,i)=>{routes[i]=route;allocated[i]={relationship:route.relationship,source:route.source,target:route.target,sourceAttachment:route.sourceAttachment,targetAttachment:route.targetAttachment};});
          }
        }
      }
    }
  }
  return routes.sort((a, b) => a.relationship.relationshipId.localeCompare(b.relationship.relationshipId));
}
/** Persist every internal route point after a direct edit. Endpoints stay attached;
 * changed segments are joined using their original rectilinear direction. */
export function routeEditWaypoints(
  path: Point[], kind: "segment" | "bend", index: number, delta: Point,
): Point[] {
  if (path.length < 2 || !Number.isFinite(delta.x) || !Number.isFinite(delta.y)) return path.slice(1, -1).map((point) => ({ ...point }));
  const moved = (point: Point) => ({ x: Math.round(point.x + delta.x), y: Math.round(point.y + delta.y) });
  const nodes = path.map((point) => ({ ...point }));
  if (kind === "bend") {
    if (index < 1 || index >= path.length - 1) return nodes.slice(1, -1);
    nodes[index] = moved(nodes[index]!);
  } else {
    if (index < 0 || index >= path.length - 1) return nodes.slice(1, -1);
    // Translating an endpoint segment creates a dogleg: the actual attachment
    // remains at the entity boundary, rather than being pulled into empty space.
    if (index > 0) nodes[index] = moved(nodes[index]!);
    if (index + 1 < nodes.length - 1) nodes[index + 1] = moved(nodes[index + 1]!);
  }
  const full: Point[] = [nodes[0]!];
  for (let i = 1; i < nodes.length; i++) {
    const a = full.at(-1)!, b = nodes[i]!;
    const originallyHorizontal = Math.abs(path[i - 1]!.y - path[i]!.y) < EPS;
    if (kind === "segment" && index === i - 1) {
      const first = moved(path[i - 1]!), last = moved(path[i]!);
      if (i === 1) {
        // Keep a short outward section at the owning entity, then connect to the
        // translated line. The router will avoid intervening boxes if necessary.
        const start = path[0]!, next = path[1]!;
        const terminal = originallyHorizontal ? { x: start.x + Math.sign(next.x - start.x) * 28, y: start.y }
          : { x: start.x, y: start.y + Math.sign(next.y - start.y) * 28 };
        full.push(terminal, originallyHorizontal ? { x: terminal.x, y: first.y } : { x: first.x, y: terminal.y }, first);
      }
      full.push(last);
      if (i === nodes.length - 1) {
        const end = path.at(-1)!, previous = path.at(-2)!;
        const terminal = originallyHorizontal ? { x: end.x + Math.sign(previous.x - end.x) * 28, y: end.y }
          : { x: end.x, y: end.y + Math.sign(previous.y - end.y) * 28 };
        full.push(originallyHorizontal ? { x: terminal.x, y: last.y } : { x: last.x, y: terminal.y }, terminal, end);
      }
    } else {
      if (Math.abs(a.x - b.x) >= EPS && Math.abs(a.y - b.y) >= EPS) {
        // At the target, keep the final incoming orientation. Everywhere else
        // preserve the original outgoing orientation from the preceding bend.
        const horizontalFirst = i === nodes.length - 1 ? !originallyHorizontal : originallyHorizontal;
        full.push(horizontalFirst ? { x: b.x, y: a.y } : { x: a.x, y: b.y });
      }
      full.push(b);
    }
  }
  return simplifyPath(full).slice(1, -1);
}

export function manualConnectorChanges(route: RoutedRelationship, waypoints: Point[]): Partial<Connector> {
  const fraction = (end: "source" | "target") => {
    const box = route[end], attachment = route[`${end}Attachment`];
    const horizontal = attachment.side === "top" || attachment.side === "bottom";
    return Math.max(0, Math.min(1, horizontal ? (attachment.point.x - box.x) / box.width : (attachment.point.y - box.y) / box.height));
  };
  return { routing: "manual", sourceSide: route.sourceAttachment.side, sourceSlot: route.sourceAttachment.slot,
    targetSide: route.targetAttachment.side, targetSlot: route.targetAttachment.slot,
    sourceFraction: fraction("source"), targetFraction: fraction("target"), waypoints: waypoints.map((point) => ({ ...point })) };
}

const automaticConnector = (connector: Connector): Connector => {
  const { sourceSide: _sourceSide, targetSide: _targetSide, sourceSlot: _sourceSlot, targetSlot: _targetSlot,
    sourceFraction: _sourceFraction, targetFraction: _targetFraction, waypoints: _waypoints, ...rest } = connector;
  return { ...rest, routing: "auto", waypoints: [] };
};

/** Pure preview helper, also used to build the committed connector.set operations.
 * Scope is object IDs. A scoped layout may recalculate relationships touching its
 * scope, but never unrelated routes. Checkpoint previews use saved connectors. */
export function connectorsForAutoLayout(
  connectors: Connector[], relationships: Relationship[], scopeObjectIds?: readonly string[],
): Connector[] {
  const scope = scopeObjectIds?.length ? new Set(scopeObjectIds) : undefined;
  const affected = new Set(relationships.filter((relationship) => !scope || scope.has(relationship.sourceObjectId) || scope.has(relationship.targetObjectId)).map((relationship) => relationship.relationshipId));
  return connectors.map((connector) => affected.has(connector.relationshipId) ? automaticConnector(connector) : { ...connector });
}

/** Invoke only for deliberate move/resize commits, not from rendering, loading or
 * preference changes. Caller batches these changes with appearance changes for
 * atomic Save and Undo/Redo. Undefined properties explicitly remove old anchors. */
export function connectorUpdatesForMovedBoxes(
  boxesBefore: CanvasBox[], boxesAfter: CanvasBox[], relationships: Relationship[], connectors: Connector[], preserveManualRoutes = true,
): { relationshipId: string; changes: Partial<Connector> }[] {
  const before = new Map(boxesBefore.map(box => [box.id, box]));
  const moved = new Set(boxesAfter.filter(box => {
    const old = before.get(box.id);
    return !old || distance(old,box)>EPS || Math.abs(old.width-box.width)>EPS || Math.abs(old.height-box.height)>EPS;
  }).map(box=>box.objectId));
  if (!moved.size) return [];
  const clearAnchors: Partial<Connector> = { routing: "auto", waypoints: [], sourceSide: undefined, targetSide: undefined, sourceSlot: undefined, targetSlot: undefined, sourceFraction: undefined, targetFraction: undefined };
  const updates: {relationshipId:string;changes:Partial<Connector>}[]=[];
  for(const connector of connectors) {
    const relationship=relationships.find(r=>r.relationshipId===connector.relationshipId);
    if(!relationship)continue;
    const endpointMoved=moved.has(relationship.sourceObjectId)||moved.has(relationship.targetObjectId);
    const manual=connector.routing==="manual" || connector.routing===undefined && (connector.sourceSide!==undefined || connector.targetSide!==undefined || !!connector.waypoints?.length);
    if(endpointMoved && (!preserveManualRoutes || !manual)) {
      if(manual || connector.sourceSide!==undefined || connector.targetSide!==undefined || connector.sourceSlot!==undefined || connector.targetSlot!==undefined || connector.sourceFraction!==undefined || connector.targetFraction!==undefined) updates.push({relationshipId:connector.relationshipId,changes:clearAnchors});
      continue;
    }
    // A third entity can invalidate a protected route just as a moved endpoint can.
    if(manual) {
      const allocation=allocateRelationships(boxesAfter,relationships,[...connectors.filter(c=>c!==connector),{...connector,routing:"manual"}]).find(edge=>edge.relationship.relationshipId===connector.relationshipId);
      if(!allocation)continue;
      const candidate=buildRelationshipRoute(allocation,0,boxesAfter,{...connector,routing:"manual"},[]);
      if(candidate.warnings.length || !routeAvoidsEntities(candidate.points,boxesAfter)) {
        const repaired=repairConnectorRoute(boxesAfter,relationships,{...connector,routing:"manual"},connectors);
        if(!repaired)throw new Error("The relationship cannot be routed around these entities. Increase spacing or choose another position.");
        updates.push({relationshipId:connector.relationshipId,changes:{...clearAnchors,...repaired}});
      }
    }
  }
  return updates;
}

export function routeAvoidsEntities(points: readonly Point[], boxes: readonly CanvasBox[]): boolean {
  return points.slice(1).every((point,index)=>boxes.every(box=>!segmentHitsBox(points[index]!,point,box)));
}

/** Move an invalid waypoint to the nearest free boundary. Keep valid stops fixed. */
function clearWaypoint(point:Point, boxes:CanvasBox[]):Point {
  let result={...point};
  for(let attempt=0;attempt<boxes.length+1;attempt++) {
    const enclosing=boxes.find(box=>inside(result,box,10));
    if(!enclosing)return result;
    const candidates=[{x:enclosing.x-12,y:result.y},{x:enclosing.x+enclosing.width+12,y:result.y},
      {x:result.x,y:enclosing.y-12},{x:result.x,y:enclosing.y+enclosing.height+12}];
    candidates.sort((a,b)=>Number(boxes.some(box=>inside(a,box,10)))-Number(boxes.some(box=>inside(b,box,10))) || distance(a,point)-distance(b,point));
    result=candidates[0]!;
  }
  return result;
}

/** A direct edit is committed only when its complete orthogonal route is valid.
 * User stops are repaired locally first; automatic routing is the last fallback.
 */
export function repairConnectorRoute(boxes:CanvasBox[], relationships:Relationship[], connector:Connector, otherConnectors:Connector[]=[]):Connector|null {
  const relationship=relationships.find(r=>r.relationshipId===connector.relationshipId);
  if(!relationship)return null;
  const waypoints=simplifyPath((connector.waypoints??[]).filter(p=>Number.isFinite(p.x)&&Number.isFinite(p.y)).map(p=>clearWaypoint(p,boxes)));
  // Remove loops produced when a dragged bend doubles back through an earlier stop.
  const stops:Point[]=[];
  for(const point of waypoints){const repeated=stops.findIndex(p=>distance(p,point)<EPS);if(repeated>=0)stops.splice(repeated+1);else stops.push(point);}
  const candidates:Connector[]=[{...connector,waypoints:simplifyPath(stops)}, {...connector,waypoints:[]},automaticConnector(connector)];
  for(const candidate of candidates) {
    const allocation=allocateRelationships(boxes,relationships,[...otherConnectors.filter(c=>c.relationshipId!==connector.relationshipId),candidate]).find(edge=>edge.relationship.relationshipId===connector.relationshipId);
    if(!allocation)continue;
    const route=buildRelationshipRoute(allocation,0,boxes,candidate,[]);
    if(!route.warnings.length&&routeAvoidsEntities(route.points,boxes))return candidate;
  }
  return null;
}

export function pathToSvg(points: Point[]) {
  return points
    .map(
      (p, i) =>
        `${i ? "L" : "M"} ${Number(p.x.toFixed(2))} ${Number(p.y.toFixed(2))}`,
    )
    .join(" ");
}
export function pathMidpoint(points: Point[]): Point {
  let remain = pathLength(points) / 2;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!,
      b = points[i]!,
      d = distance(a, b);
    if (remain <= d && d > 0)
      return {
        x: a.x + ((b.x - a.x) * remain) / d,
        y: a.y + ((b.y - a.y) * remain) / d,
      };
    remain -= d;
  }
  return points[0] ?? { x: 0, y: 0 };
}
/** A stable route-relative anchor; its normal uses a readable tangent so ordinary
 * source/target direction changes do not unexpectedly flip above and below. */
export function relationshipLabelAnchor(points: Point[], label: RelationshipLabelStyle = {}) {
  const total = points.slice(1).reduce((sum, b, i) => sum + Math.hypot(b.x - points[i]!.x, b.y - points[i]!.y), 0);
  let remaining = total * Math.max(0, Math.min(1, label.position ?? 0.5));
  let point = points[0] ?? { x: 0, y: 0 }, tangent = { x: 1, y: 0 };
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!, b = points[i]!, length = Math.hypot(b.x - a.x, b.y - a.y);
    if (!length) continue;
    if (remaining <= length || i === points.length - 1) {
      const fraction = Math.min(1, remaining / length);
      point = { x: a.x + (b.x - a.x) * fraction, y: a.y + (b.y - a.y) * fraction };
      tangent = { x: (b.x - a.x) / length, y: (b.y - a.y) / length };
      break;
    }
    remaining -= length;
  }
  // Prefer left-to-right or bottom-to-top for upright aligned text.
  if (tangent.x < -EPS || (Math.abs(tangent.x) <= EPS && tangent.y > 0))
    tangent = { x: -tangent.x, y: -tangent.y };
  const placement = label.placement ?? "above";
  const offset = placement === "on-line" ? 0 : Math.max(0, label.offset ?? 16) * (placement === "below" ? -1 : 1);
  return {
    x: point.x + tangent.y * offset,
    y: point.y - tangent.x * offset,
    angle: label.orientation === "vertical" ? -90 : label.orientation === "aligned" ? Math.atan2(tangent.y, tangent.x) * 180 / Math.PI : 0,
    routePoint: point,
  };
}

let labelMeasurement: CanvasRenderingContext2D | null | undefined;
export function measureRelationshipLabel(text:string,fontFamily="Arial",fontSize=11):number {
  if(labelMeasurement===undefined) labelMeasurement=typeof document!=="undefined" ? document.createElement("canvas").getContext("2d") : null;
  if(labelMeasurement){labelMeasurement.font=`500 ${fontSize}px ${fontFamily}`;return labelMeasurement.measureText(text).width;}
  return [...text].reduce((n,c)=>n+("MW@%".includes(c)?.9:"il.,' ".includes(c)?.3:.6)*fontSize,0);
}

/** Label layout shared by SVG export and the live canvas. Segment selection is
 * deterministic, prefers horizontal room, and uses actual canvas text metrics.
 */
export function layoutRelationshipLabel(points:Point[],label:RelationshipLabelStyle,caption:string,fontFamily="Arial",fontSize=11,boxes:readonly CanvasBox[]=[]) {
  const padding=18, width=measureRelationshipLabel(caption,fontFamily,fontSize)+12, lineHeight=fontSize+5;
  const base=relationshipLabelAnchor(points,label);
  const segments=points.slice(1).map((b,index)=>{const a=points[index]!;return {a,b,index,length:distance(a,b),horizontal:Math.abs(a.y-b.y)<EPS};}).filter(s=>s.length>0);
  const anchorAt=(segment:typeof segments[number],textWidth:number) => {
    // The existing position slider expresses a preferred point. Clamp it within
    // this segment so the entire caption remains clear of bends and end marks.
    const horizontal=segment.horizontal,low=horizontal?Math.min(segment.a.x,segment.b.x):Math.min(segment.a.y,segment.b.y);
    const high=horizontal?Math.max(segment.a.x,segment.b.x):Math.max(segment.a.y,segment.b.y);
    const preferred=horizontal?base.routePoint.x:base.routePoint.y;
    const extent=label.orientation==="vertical" ? horizontal?lineHeight:textWidth : label.orientation==="horizontal" ? horizontal?textWidth:lineHeight : textWidth;
    const along=Math.max(low+extent/2+padding,Math.min(high-extent/2-padding,preferred));
    const routePoint=horizontal?{x:along,y:segment.a.y}:{x:segment.a.x,y:along};
    const angle=label.orientation==="vertical"?-90:label.orientation==="horizontal"?0:horizontal?0:-90;
    const off=label.placement==="on-line"?0:(label.offset??16)*(label.placement==="below"?-1:1);
    return {...routePoint,x:routePoint.x+(horizontal?0:-off),y:routePoint.y-(horizontal?off:0),angle,routePoint};
  };
  const ranked=[...segments].sort((a,b)=>Number(b.horizontal)-Number(a.horizontal)||distance({x:(a.a.x+a.b.x)/2,y:(a.a.y+a.b.y)/2},base.routePoint)-distance({x:(b.a.x+b.b.x)/2,y:(b.a.y+b.b.y)/2},base.routePoint)||a.index-b.index);
  for(const segment of ranked){
    const extent=label.orientation==="vertical" ? segment.horizontal?lineHeight:width : label.orientation==="horizontal" ? segment.horizontal?width:lineHeight : width;
    if(segment.length<extent+padding*2)continue;
    const anchor=anchorAt(segment,width),rotated=Math.abs(anchor.angle)===90;
    const box={id:"label",objectId:"label",x:anchor.x-(rotated?lineHeight:width)/2,y:anchor.y-(rotated?width:lineHeight)/2,width:rotated?lineHeight:width,height:rotated?width:lineHeight};
    if(boxes.some(other=>overlaps(box,other,4)))continue;
    return {...anchor,width,height:lineHeight,lines:[caption],fallback:false,segment:segment.index};
  }
  const longest=[...segments].sort((a,b)=>b.length-a.length||a.index-b.index)[0];
  const maxWidth=Math.max(72,Math.min(180,(longest?.length??180)-padding*2));
  const lines:string[]=[];let current="";
  for(const word of caption.split(/\s+/)){
    if(current&&measureRelationshipLabel(`${current} ${word}`,fontFamily,fontSize)>maxWidth){lines.push(current);current="";}
    for(const char of (current?" ":"")+word){if(current&&measureRelationshipLabel(current+char,fontFamily,fontSize)>maxWidth){lines.push(current);current="";}current+=char;}
  }
  if(current||!lines.length)lines.push(current);
  const actualWidth=Math.max(...lines.map(line=>measureRelationshipLabel(line,fontFamily,fontSize)))+12,height=lines.length*lineHeight+8;
  const routePoint=longest?{x:(longest.a.x+longest.b.x)/2,y:(longest.a.y+longest.b.y)/2}:base.routePoint;
  const positions=[{x:routePoint.x,y:routePoint.y-height/2-10},{x:routePoint.x,y:routePoint.y+height/2+10},
    {x:routePoint.x+actualWidth/2+10,y:routePoint.y},{x:routePoint.x-actualWidth/2-10,y:routePoint.y}];
  const position=positions.find(p=>!boxes.some(other=>overlaps({id:"label",objectId:"label",x:p.x-actualWidth/2,y:p.y-height/2,width:actualWidth,height},other,4)))??positions[0]!;
  return {...position,angle:0,routePoint,width:actualWidth,height,lines,fallback:true,segment:longest?.index??0};
}

export type Alignment = "left" | "center" | "right" | "top" | "middle" | "bottom" | "horizontal" | "vertical";
/** Align/distribute selected movable appearances without ever moving pinned ones. */
export function arrangeBoxes(boxes: CanvasBox[], action: Alignment): Record<string, Point> {
  const movable = boxes.filter((box) => !box.pinned);
  if (boxes.length < 2 || !movable.length) return {};
  const left = Math.min(...boxes.map((box) => box.x));
  const right = Math.max(...boxes.map((box) => box.x + box.width));
  const top = Math.min(...boxes.map((box) => box.y));
  const bottom = Math.max(...boxes.map((box) => box.y + box.height));
  if (action === "horizontal" || action === "vertical") {
    if (movable.length < 3) return {};
    const horizontal = action === "horizontal", axis = horizontal ? "x" : "y", size = horizontal ? "width" : "height";
    const sorted = [...movable].sort((a, b) => a[axis] - b[axis] || a.id.localeCompare(b.id));
    const start = sorted[0]![axis], end = sorted.at(-1)![axis] + sorted.at(-1)![size];
    const gap = (end - start - sorted.reduce((sum, box) => sum + box[size], 0)) / (sorted.length - 1);
    let cursor = start;
    return Object.fromEntries(sorted.map((box) => {
      const point = { x: box.x, y: box.y, [axis]: Math.round(cursor) };
      cursor += box[size] + gap;
      return [box.id, point];
    }));
  }
  return Object.fromEntries(movable.map((box) => [box.id, {
    x: Math.round(action === "left" ? left : action === "right" ? right - box.width : action === "center" ? (left + right - box.width) / 2 : box.x),
    y: Math.round(action === "top" ? top : action === "bottom" ? bottom - box.height : action === "middle" ? (top + bottom - box.height) / 2 : box.y),
  }]));
}

export function overlaps(a: CanvasBox, b: CanvasBox, gap = 0) {
  return (
    a.x < b.x + b.width + gap &&
    a.x + a.width + gap > b.x &&
    a.y < b.y + b.height + gap &&
    a.y + a.height + gap > b.y
  );
}
export function geometryFingerprint(diagram: Diagram) {
  return JSON.stringify([
    diagram.diagramVersion,
    diagram.appearances.map((a) => [
      a.appearanceId,
      a.position,
      a.width,
      a.height,
      a.pinned,
      a.collapsed,
    ]),
    diagram.connectors,
  ]);
}

/** Deterministic layouts use measured bounds. Pins and out-of-scope objects remain
 * hard obstacles; collisions are resolved before preview, never by mutating input. */
export function createLayout(
  boxes: CanvasBox[], relationships: Relationship[], options: LayoutOptions, fingerprint = "",
): LayoutCandidate {
  const sorted = [...boxes].sort((a, b) => a.id.localeCompare(b.id));
  const scope = options.scopeIds?.length ? new Set(options.scopeIds) : undefined;
  const movable = sorted.filter((box) => !box.pinned && (!scope || scope.has(box.id) || scope.has(box.objectId)));
  const movableIds = new Set(movable.map((box) => box.id));
  const fixed = sorted.filter((box) => !movableIds.has(box.id));
  const byObject = new Map<string, CanvasBox>();
  sorted.forEach((box) => { if (!byObject.has(box.objectId)) byObject.set(box.objectId, box); });
  const byId = new Map(sorted.map((box) => [box.id, box]));
  const adjacency = new Map(sorted.map((box) => [box.id, new Set<string>()]));
  const children = new Map(sorted.map((box) => [box.id, new Set<string>()]));
  [...relationships].sort((a, b) => a.relationshipId.localeCompare(b.relationshipId)).forEach((relationship) => {
    const child = byObject.get(relationship.sourceObjectId), parent = byObject.get(relationship.targetObjectId);
    if (!child || !parent || child.id === parent.id) return;
    adjacency.get(child.id)!.add(parent.id); adjacency.get(parent.id)!.add(child.id);
    children.get(parent.id)!.add(child.id);
  });
  const visited = new Set<string>(), components: CanvasBox[][] = [];
  for (const box of sorted) {
    if (visited.has(box.id)) continue;
    const queue = [box], component: CanvasBox[] = [];
    visited.add(box.id);
    for (let i = 0; i < queue.length; i++) {
      const current = queue[i]!; component.push(current);
      for (const id of [...adjacency.get(current.id)!].sort()) if (!visited.has(id)) {
        visited.add(id); queue.push(byId.get(id)!);
      }
    }
    components.push(component.sort((a, b) => a.id.localeCompare(b.id)));
  }
  const groups: CanvasBox[][] = [];
  if (options.mode === "clustered") {
    const classified = new Map<string, CanvasBox[]>();
    components.forEach((component, i) => component.forEach((box) => {
      const key = box.group || `component-${i}`;
      classified.set(key, [...(classified.get(key) ?? []), box]);
    }));
    for (const group of classified.values()) {
      if (group.length <= 10) { groups.push(group); continue; }
      const remaining = new Set(group.map((box) => box.id));
      while (remaining.size) {
        const seed = group.filter((box) => remaining.has(box.id)).sort((a, b) =>
          adjacency.get(b.id)!.size - adjacency.get(a.id)!.size || a.id.localeCompare(b.id))[0]!;
        const ids = [seed.id, ...[...adjacency.get(seed.id)!].sort()].filter((id) => remaining.has(id)).slice(0, 8);
        groups.push(ids.map((id) => byId.get(id)!)); ids.forEach((id) => remaining.delete(id));
      }
    }
  } else groups.push(...components);
  const gap = Math.max(40, Number.isFinite(options.gap) ? options.gap : 80);
  const positions: Record<string, Point> = {}, warnings: string[] = [];
  const occupied = fixed.map((box) => ({ ...box }));
  let offsetX = 80, offsetY = 80, rowHeight = 0;
  const place = (box: CanvasBox, desired: Point) => {
    if (!movableIds.has(box.id)) return;
    let candidate = { ...box, x: Math.round(desired.x), y: Math.round(desired.y) };
    // Search nearby positions by increasing displacement. Fixed entities are never
    // moved to make room; if this bounded neighborhood is full, use free outer space.
    if (occupied.some((other) => overlaps(candidate, other, 48))) {
      const alternatives: Point[] = [];
      for (let ring = 1; ring <= 12; ring++) {
        const xStep = Math.max(box.width + gap, 100), yStep = Math.max(box.height + gap, 100);
        for (let step = -ring; step <= ring; step++) {
          alternatives.push({ x: desired.x + step * xStep, y: desired.y - ring * yStep });
          alternatives.push({ x: desired.x + step * xStep, y: desired.y + ring * yStep });
          if (Math.abs(step) < ring) {
            alternatives.push({ x: desired.x - ring * xStep, y: desired.y + step * yStep });
            alternatives.push({ x: desired.x + ring * xStep, y: desired.y + step * yStep });
          }
        }
      }
      alternatives.sort((a, b) => distance(a, desired) - distance(b, desired) || a.y - b.y || a.x - b.x);
      const free = alternatives.find((point) => !occupied.some((other) => overlaps({ ...candidate, x: Math.round(point.x), y: Math.round(point.y) }, other, 48)));
      if (free) candidate = { ...candidate, x: Math.round(free.x), y: Math.round(free.y) };
      else {
        candidate.x = Math.ceil(Math.max(desired.x, ...occupied.map((other) => other.x + other.width)) + gap);
        warnings.push("Some entities were placed outside the main arrangement because fixed objects filled the nearby space.");
      }
    }
    occupied.push(candidate); positions[box.id] = { x: candidate.x, y: candidate.y };
  };
  groups.forEach((group, index) => {
    const local = componentLayout(group, adjacency, children, options);
    warnings.push(...local.warnings);
    const minX = Math.min(...group.map((box) => local.positions[box.id]!.x));
    const minY = Math.min(...group.map((box) => local.positions[box.id]!.y));
    const maxX = Math.max(...group.map((box) => local.positions[box.id]!.x + box.width));
    const maxY = Math.max(...group.map((box) => local.positions[box.id]!.y + box.height));
    const anchors = group.filter((box) => !movableIds.has(box.id));
    const median = (values: number[]) => [...values].sort((a, b) => a - b)[Math.floor(values.length / 2)]!;
    const shift = anchors.length ? {
      x: median(anchors.map((box) => box.x - local.positions[box.id]!.x)),
      y: median(anchors.map((box) => box.y - local.positions[box.id]!.y)),
    } : { x: offsetX - minX, y: offsetY - minY };
    for (const box of group) place(box, { x: local.positions[box.id]!.x + shift.x, y: local.positions[box.id]!.y + shift.y });
    offsetX += maxX - minX + gap * 2; rowHeight = Math.max(rowHeight, maxY - minY);
    if ((index + 1) % 3 === 0) { offsetX = 80; offsetY += rowHeight + gap * 2; rowHeight = 0; }
  });
  const result = sorted.map((box) => ({ ...box, ...positions[box.id] }));
  let overlapCount = 0;
  for (let i = 0; i < result.length; i++) for (let j = i + 1; j < result.length; j++)
    if (overlaps(result[i]!, result[j]!)) overlapCount++;
  if (overlapCount) warnings.push("Pinned or fixed entities overlap. Their positions were preserved.");
  return {
    positions, moved: movable.filter((box) => positions[box.id] && distance(positions[box.id]!, box) > 1).length,
    overlaps: overlapCount, warnings: [...new Set(warnings)], clusters: groups.length, fingerprint,
  };
}
export function boxesFromAppearances(
  appearances: Appearance[],
  sizes: Record<string, { width: number; height: number }> = {},
): CanvasBox[] {
  return appearances.map((a) => ({
    id: a.appearanceId,
    objectId: a.objectId,
    x: a.position.x,
    y: a.position.y,
    width: sizes[a.appearanceId]?.width ?? a.width ?? 260,
    height: sizes[a.appearanceId]?.height ?? a.height ?? 160,
    pinned: a.pinned,
  }));
}

export function projectToPath(
  point: Point,
  path: Point[],
): { point: Point; progress: number; distance: number } {
  let best = { point: path[0] ?? point, progress: 0, distance: Infinity },
    progress = 0;
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1]!,
      b = path[i]!,
      horizontal = Math.abs(a.y - b.y) < EPS;
    const projected = horizontal
      ? {
          x: Math.max(
            Math.min(a.x, b.x),
            Math.min(Math.max(a.x, b.x), point.x),
          ),
          y: a.y,
        }
      : {
          x: a.x,
          y: Math.max(
            Math.min(a.y, b.y),
            Math.min(Math.max(a.y, b.y), point.y),
          ),
        };
    const d = distance(projected, point);
    if (d < best.distance)
      best = {
        point: projected,
        progress: progress + distance(a, projected),
        distance: d,
      };
    progress += distance(a, b);
  }
  return best;
}
export function insertWaypoint(
  path: Point[],
  waypoints: Point[],
  point: Point,
): Point[] {
  const projected = projectToPath(point, path);
  if (waypoints.some((p) => distance(p, projected.point) < 2))
    return [...waypoints];
  const insertion = waypoints.findIndex(
    (p) => projectToPath(p, path).progress > projected.progress,
  );
  const result = [...waypoints];
  result.splice(insertion < 0 ? result.length : insertion, 0, {
    x: Math.round(projected.point.x),
    y: Math.round(projected.point.y),
  });
  return result;
}
export function countRouteCrossings(routes: { points: Point[] }[]) {
  let count = 0;
  for (let i = 0; i < routes.length; i++)
    for (let j = i + 1; j < routes.length; j++) {
      const a = routes[i]!.points,
        b = routes[j]!.points;
      for (let ai = 1; ai < a.length; ai++)
        for (let bi = 1; bi < b.length; bi++) {
          const a0 = a[ai - 1]!,
            a1 = a[ai]!,
            b0 = b[bi - 1]!,
            b1 = b[bi]!;
          const ah = Math.abs(a0.y - a1.y) < EPS,
            bh = Math.abs(b0.y - b1.y) < EPS;
          if (ah === bh) continue;
          const h0 = ah ? a0 : b0,
            h1 = ah ? a1 : b1,
            v0 = ah ? b0 : a0,
            v1 = ah ? b1 : a1;
          if (
            v0.x > Math.min(h0.x, h1.x) + EPS &&
            v0.x < Math.max(h0.x, h1.x) - EPS &&
            h0.y > Math.min(v0.y, v1.y) + EPS &&
            h0.y < Math.max(v0.y, v1.y) - EPS
          )
            count++;
        }
    }
  return count;
}

/** Explicit publication only: personal font rendering never invokes this operation builder. */
export function layoutOperations(
  diagram: Diagram,
  candidate: LayoutCandidate,
  boxes: CanvasBox[],
  preferences: Pick<Preferences, "fontFamily" | "fontSize" | "titleSize">,
): Operation[] {
  const operations: Operation[] = [];
  for (const box of boxes) {
    const appearance = diagram.appearances.find(
      (a) => a.appearanceId === box.id,
    );
    if (!appearance) continue;
    const next = candidate.positions[box.id];
    const changes: Record<string, unknown> = {};
    const locked = appearance.sizeLocked ?? (appearance.width !== undefined || appearance.height !== undefined);
    if (next && !appearance.pinned && distance(appearance.position, next) > 0.1)
      changes.position = next;
    if (!locked) {
      if (appearance.width !== box.width) changes.width = box.width;
      if (appearance.height !== box.height) changes.height = box.height;
    }
    if (appearance.sizeLocked === undefined) changes.sizeLocked = locked;
    if (Object.keys(changes).length)
      operations.push({
        type: "appearance.update",
        diagramId: diagram.diagramId,
        appearanceId: appearance.appearanceId,
        changes,
      });
  }
  operations.push({
    type: "diagram.update",
    diagramId: diagram.diagramId,
    changes: {
      layoutProfile: {
        ...diagram.layoutProfile,
        version: 1,
        geometryContext: "shared-publication",
        fontFamily: preferences.fontFamily,
        fontSize: preferences.fontSize,
        titleSize: preferences.titleSize,
        measuredBounds: Object.fromEntries(
          boxes.map((b) => [b.id, { width: b.width, height: b.height }]),
        ),
      },
    },
  });
  return operations;
}
